import { useSelector, useDispatch } from "react-redux";
import {
  addIngredient,
  removeIngredient,
  setIngredientError,
  setInputValue,
} from "../../redux/actions/actions";
import {
  selectIngredients,
  selectIngredientErrors,
  selectInputValue,
} from "../../redux/selectors/selectors";
import { errorMessages, inputGuides } from "../../helpers/helper_objects";
import {
  TextField,
  IconButton,
  FormLabel,
  Box,
  Typography,
} from "@mui/material";
import { Delete, AddCircle } from "@mui/icons-material";
import { resetIngredientsErrors } from "../../helpers/helper_functions";

const IngredientList = () => {
  const dispatch = useDispatch();
  const ingredients = useSelector(selectIngredients);
  const ingredientErrors = useSelector(selectIngredientErrors);
  const inputValue = useSelector(selectInputValue);

  const handleAddIngredient = () => {
    resetIngredientsErrors(dispatch, setIngredientError, true);
    const newIngredient = inputValue.trim().toLowerCase();

    if (newIngredient) {
      const isInvalidIngredient =
        typeof newIngredient !== "string" ||
        !/^[a-zA-Z ]+$/.test(newIngredient);

      dispatch(
        setIngredientError(
          "existingIngredient",
          ingredients.includes(
            newIngredient.endsWith("s")
              ? newIngredient.slice(0, -1)
              : `${newIngredient}s`,
          ) || ingredients.includes(newIngredient),
        ),
      );

      dispatch(setIngredientError("invalidIngredient", isInvalidIngredient));

      if (
        !isInvalidIngredient &&
        !ingredients.includes(
          newIngredient.endsWith("s")
            ? newIngredient.slice(0, -1)
            : `${newIngredient}s`,
        ) &&
        !ingredients.includes(newIngredient)
      ) {
        dispatch(addIngredient(newIngredient));
      }

      dispatch(setInputValue(""));
    }
  };

  const handleRemoveIngredient = (ingredient) => {
    dispatch(removeIngredient(ingredient));
  };

  const hasError =
    ingredientErrors.emptyIngredients ||
    ingredientErrors.invalidIngredient ||
    ingredientErrors.existingIngredient;

  const helperText =
    ingredientErrors.emptyIngredients && !ingredientErrors.invalidIngredient
      ? errorMessages.noAddedIngredients
      : ingredientErrors.existingIngredient
        ? errorMessages.duplicate
        : ingredientErrors.invalidIngredient
          ? errorMessages.invalidIngredient
          : "";

  return (
    <Box data-testid="ingredients-list-component" sx={{ width: "100%" }}>
      <FormLabel
        htmlFor="ingredientInput"
        data-testid="added-ingredients-id"
        sx={{
          display: "block",
          mb: 1,
          color: "rgba(255,255,255,0.78)",
          fontWeight: 800,
          letterSpacing: 0.6,
          textTransform: "uppercase",
          fontSize: 12,
        }}
      >
        Added ingredients
      </FormLabel>

      {/* Chips */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          mb: 1.5,
        }}
      >
        {ingredients.length === 0 ? (
          <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: 13 }}>
            No ingredients added yet.
          </Typography>
        ) : (
          ingredients.map((ingredient, index) => (
            <Box
              key={`${ingredient}-${index}`}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                px: 1.2,
                py: 0.8,
                borderRadius: 999,
                background: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.12)",
                backdropFilter: "blur(10px)",
              }}
            >
              <Typography
                sx={{ color: "rgba(255,255,255,0.92)", fontSize: 13 }}
              >
                {ingredient}
              </Typography>

              <IconButton
                onClick={() => handleRemoveIngredient(ingredient)}
                aria-label="delete"
                data-testid="delete-btn"
                size="small"
                sx={{
                  p: 0.4,
                  "&:hover": { background: "rgba(255,82,82,0.15)" },
                }}
              >
                <Delete
                  fontSize="small"
                  sx={{
                    color: "#ff5252",
                    transition: "color 120ms ease",
                    ".MuiIconButton-root:hover &": { color: "#ffb74d" }, // hover color
                  }}
                />
              </IconButton>
            </Box>
          ))
        )}
      </Box>

      {/* Input */}
      <TextField
        data-testid="ingredients-input-id"
        id="ingredientInput"
        type="text"
        fullWidth
        value={inputValue}
        onChange={(e) => dispatch(setInputValue(e.target.value))}
        onKeyUp={(e) => {
          if (e.key === "Enter") handleAddIngredient();
          else resetIngredientsErrors(dispatch, setIngredientError, true);
        }}
        label={inputGuides.ingredientsInput}
        error={hasError}
        helperText={helperText}
        InputLabelProps={{
          sx: {
            color: "",
            "&.Mui-focused": {
              color: "",
              "&.Mui-focused": { color: "green" },
              "&.Mui-error": { color: "#d32f2f" },
            },
            "&.Mui-error": { color: "#d32f2f" },
          },
        }}
        FormHelperTextProps={{ sx: { color: "#ffb74d" } }}
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: 3,
            background: "white",
            color: "black",
            "& fieldset": { borderColor: "rgba(255,255,255,0.14)" },
            "&:hover fieldset": { borderColor: "rgba(255,255,255,0.24)" },
            "&.Mui-focused fieldset": { borderColor: "#ffb74d" },
          },
        }}
        InputProps={{
          endAdornment: (
            <IconButton
              data-testid="add-button-id"
              onClick={handleAddIngredient}
              sx={{
                color: "green",
                "&:hover": { background: "rgba(255,183,77,0.12)" },
              }}
            >
              <AddCircle />
            </IconButton>
          ),
        }}
      />
    </Box>
  );
};

export default IngredientList;
